import React from "react";

import { css } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { usePatientFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { formatTextToRichText, useRichTextState } from "@/hooks/useRichTextState";
import { useStyles } from "@/hooks/useStyles.ts";
import { CarePlan, getTelemonitoringTierTranslation, getTelemonitoringTierColor } from "@/models/CarePlanModel.ts";
import {
  Condition,
  diabetesTypeTranslationMapping,
  medicationSchemaTranslationMapping,
  diabetesTypeColorMapping,
  medicationSchemaColorMapping,
} from "@/models/ConditionModel.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import { PatientsTagsList } from "@/pages/patients/PatientsTagsList.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { FileManager, ReadOnlyFileManager } from "@/uiKit/molecules/FileManager.tsx";
import { RichText } from "@/uiKit/molecules/RichText/RichText.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";

type CurrentCarePlanProps = {
  patientId: string;
  carePlan: CarePlan;
};

export const CurrentCarePlan: React.FC<CurrentCarePlanProps> = ({ patientId, carePlan }) => {
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.grid}>
      <div className={css({ gridRow: 1, gridColumn: 1 })}>
        <TelemonitoringInfo patientId={patientId} />
      </div>
      <div className={css({ gridRow: 1, gridColumn: 2 })}>
        <TelemonitoringDatesInfo carePlan={carePlan} />
      </div>
      <div className={css({ gridRow: 2, gridColumn: 1 })}>
        <ResponsiblePractitionerInfo patientId={patientId} />
      </div>
      <div className={css({ gridRow: 2, gridColumn: 2 })}>
        <TelemonitoringTierInfo carePlan={carePlan} />
      </div>
      <div className={css({ gridRow: 3, gridColumn: `1 / span 2` })}>
        <AttachmentsInfo carePlan={carePlan} />
      </div>
      <div className={css({ gridRow: 4, gridColumn: `1 / span 2` })}>
        <TelemonitoringGoalsInfo carePlan={carePlan} />
      </div>
    </div>
  );
};

type TelemonitoringInfoProps = {
  patientId: string;
};
export const TelemonitoringInfo: React.FC<TelemonitoringInfoProps> = ({ patientId }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const carePlans = Queries.practitioner.useCurrentCarePlanInfo(patientId);

  return (
    <div className={styles.column}>
      <Typography variant="subtitle" className={styles.labelStyle}>
        {t("diabetesForms.telemonitoring")}
      </Typography>
      <PatientsTagsList telemonitoringTags={carePlans.telemonitoringTags} />
    </div>
  );
};

type ResponsiblePractitionerInfo = {
  patientId: string;
};

export const ResponsiblePractitionerInfo: React.FC<ResponsiblePractitionerInfo> = ({ patientId }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { data: careTeam } = Queries.practitioner.useCareTeam(patientId);
  const me = Queries.user.usePractitionerMe({});

  const participants = careTeam?.participants;

  if (!participants) {
    return null;
  }

  const nurses = participants.filter(participant => participant.qualification === "nurse");
  const doctor = participants.find(participant => participant.qualification === "doctor");

  return (
    <div className={styles.column}>
      <Typography variant="subtitle" className={styles.labelStyle}>
        {me.qualification === "nurse" ? t("diabetesForms.responsibleDoctor") : t("diabetesForms.responsibleNurse")}
      </Typography>
      {me.qualification === "nurse" ? (
        doctor ? (
          <Typography variant="body">{userFormattedName(doctor.familyName, doctor.givenName)}</Typography>
        ) : null
      ) : nurses.length > 0 ? (
        <Typography variant="body">
          {nurses.map(nurse => userFormattedName(nurse.familyName, nurse.givenName)).join(", ")}
        </Typography>
      ) : null}
    </div>
  );
};

type InfoProps = {
  carePlan: CarePlan;
};

type InfoPropsCondition = {
  condition: Condition;
};

export const DiabetesTypeInfo: React.FC<Partial<InfoPropsCondition>> = ({ condition }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  return (
    <div className={styles.column}>
      <Typography variant="subtitle">{t("diabetesForms.diabetesType")}</Typography>
      <div>
        {!!condition && (
          <Tag
            color={diabetesTypeColorMapping(condition.diabetesType, condition.medicationSchema)}
            label={t(diabetesTypeTranslationMapping(condition.diabetesType, condition.medicationSchema))}
          />
        )}
      </div>
    </div>
  );
};

export const InsulinSchemeInfo: React.FC<Partial<InfoPropsCondition>> = ({ condition }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  //
  return (
    <div className={styles.column}>
      <Typography variant="subtitle">{t("diabetesForms.insulinScheme")}</Typography>
      <div>
        {!!condition && (
          <Tag
            color={medicationSchemaColorMapping(condition.medicationSchema)}
            label={t(medicationSchemaTranslationMapping(condition.medicationSchema))}
          />
        )}
      </div>
    </div>
  );
};

export const TelemonitoringTierInfo: React.FC<InfoProps> = ({ carePlan }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.column}>
      <Typography variant="subtitle">{t("diabetesForms.tier")}</Typography>
      <div>
        {carePlan?.telemonitoringTier && (
          <Tag
            color={getTelemonitoringTierColor(carePlan.telemonitoringTier)}
            label={t(getTelemonitoringTierTranslation(carePlan.telemonitoringTier))}
          />
        )}
      </div>
    </div>
  );
};

export const TermDateInfo: React.FC<Partial<InfoPropsCondition>> = ({ condition }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  if (!condition || !condition.termDate) {
    return null;
  }

  return (
    <div className={styles.column}>
      <Typography variant="subtitle" className={styles.labelStyle}>
        {t("diabetesForms.termDate")}
      </Typography>
      {!!condition && (
        <Typography variant="body">{DateTime.fromISO(condition.termDate).toFormat("dd/MM/yy")}</Typography>
      )}
    </div>
  );
};

const TelemonitoringGoalsInfo: React.FC<InfoProps> = ({ carePlan }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const patient = usePatientFromURL();

  const me = Queries.user.usePractitionerMe({});
  const updateTelemonitoringGoals = Queries.practitioner.useEditTherapeuticObjectives();
  const richTextState = useRichTextState(formatTextToRichText(carePlan.description), {
    onSave: text => {
      updateTelemonitoringGoals.mutate(
        {
          patientId: patient.id,
          carePlanId: carePlan.id,
          data: {
            description: text,
            descriptionAttachment: carePlan.descriptionAttachment,
          },
        },
        {
          onSuccess: () => {
            TextToast.success(t("diabetesForms.goalsUpdated"));
          },
          onError: () => {
            TextToast.error(t("errors.unknownError"));
          },
        },
      );
    },
  });

  const isNurse = me.qualification === "nurse";

  return (
    <div className={styles.column}>
      <div>
        <Typography variant="subtitle" className={styles.labelStyle}>
          {t("diabetesForms.goalsShort")}
        </Typography>
      </div>
      {!carePlan.description && !carePlan.descriptionAttachment && (
        <Typography variant="body" className={styles.noValue}>
          {t("diabetesForms.goalsNone")}
        </Typography>
      )}
      <FileManager
        id="care-plan-objectives-file"
        fileType="description"
        patientInfo={patient}
        label={t("diabetesForms.goalsShort")}
        uploadLabel={t("diabetesForms.uploadGoals")}
        defaultFile={carePlan.descriptionAttachment}
        readOnly={isNurse}
        onFilePicked={file => {
          updateTelemonitoringGoals.mutate(
            {
              patientId: patient.id,
              carePlanId: carePlan.id,
              data: {
                description: carePlan.description,
                descriptionAttachment: file,
              },
            },
            {
              onSuccess: () => {
                TextToast.success(t("diabetesForms.goalsAttachmentUpdated"));
              },
              onError: () => {
                TextToast.error(t("errors.unknownError"));
              },
            },
          );
        }}
        onDelete={() => {
          updateTelemonitoringGoals.mutate(
            {
              patientId: patient.id,
              carePlanId: carePlan.id,
              data: {
                description: carePlan.description,
                descriptionAttachment: undefined,
              },
            },
            {
              onSuccess: () => {
                TextToast.success(t("diabetesForms.goalsAttachmentDeleted"));
              },
              onError: () => {
                TextToast.error(t("errors.unknownError"));
              },
            },
          );
        }}
      />
      <RichText
        createNewTextTitle={t("diabetesForms.writeGoals")}
        editTextTitle={t("diabetesForms.editGoals")}
        richTextState={richTextState}
        disabledEdition={isNurse}
        className={styles.richText}
        hideHeader
      />
    </div>
  );
};

const TelemonitoringDatesInfo: React.FC<InfoProps> = ({ carePlan }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.column}>
      <Typography variant="subtitle" className={styles.labelStyle}>
        {t("diabetesForms.dates")}
      </Typography>
      {
        <Typography variant="body">{`${toDateTime(carePlan.periodStart).toLocaleString()} - ${
          carePlan.periodEnd ? toDateTime(carePlan.periodEnd).toLocaleString() : "..."
        }`}</Typography>
      }
    </div>
  );
};

const AttachmentsInfo: React.FC<InfoProps> = ({ carePlan }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const me = Queries.user.usePractitionerMe({});
  const isNurse = me.qualification === "nurse";
  const patient = Queries.practitioner.usePatient(carePlan.subjectId);

  const editTreatmentPrescription = Queries.practitioner.useEditTreatmentPrescription();
  const deleteTreatmentPrescription = Queries.practitioner.useDeleteTreatmentPrescription();
  const editReferralLetter = Queries.practitioner.useEditReferralLetter();
  const deleteReferralLetter = Queries.practitioner.useDeleteReferralLetter();

  return (
    <div className={styles.column}>
      <Typography variant="subtitle" className={styles.labelStyle}>
        {t("diabetesForms.attachments")}
      </Typography>
      <FileManager
        id="care-plan-treatment-prescription-file"
        label={t("diabetesForms.treatmentPrescription")}
        uploadLabel={t("diabetesForms.uploadTreatmentPrescription")}
        defaultFile={carePlan.treatmentPrescription}
        fileType="treatment"
        patientInfo={patient}
        readOnly={isNurse}
        onFilePicked={file =>
          editTreatmentPrescription.mutate(
            { patientId: carePlan.subjectId, carePlanId: carePlan.id, data: file },
            {
              onSuccess: () => {
                TextToast.success(t("diabetesForms.treatmentPrescriptionUpdated"));
              },
              onError: () => {
                TextToast.error(t("errors.unknownError"));
              },
            },
          )
        }
        onDelete={() =>
          deleteTreatmentPrescription.mutate(
            { patientId: carePlan.subjectId, carePlanId: carePlan.id },
            {
              onSuccess: () => {
                TextToast.success(t("diabetesForms.treatmentPrescriptionDeleted"));
              },
              onError: () => {
                TextToast.error(t("errors.unknownError"));
              },
            },
          )
        }
      />
      <FileManager
        id="care-plan-referral-letter-file"
        label={t("diabetesForms.referralLetter")}
        uploadLabel={t("diabetesForms.uploadReferralLetter")}
        defaultFile={carePlan.referralLetter}
        fileType="referral_letter"
        patientInfo={patient}
        readOnly={isNurse}
        onFilePicked={file =>
          editReferralLetter.mutate(
            { patientId: carePlan.subjectId, carePlanId: carePlan.id, data: file },
            {
              onSuccess: () => {
                TextToast.success(t("diabetesForms.referralLetterUpdated"));
              },
              onError: () => {
                TextToast.error(t("errors.unknownError"));
              },
            },
          )
        }
        onDelete={() =>
          deleteReferralLetter.mutate(
            { patientId: carePlan.subjectId, carePlanId: carePlan.id },
            {
              onSuccess: () => {
                TextToast.success(t("diabetesForms.referralLetterDeleted"));
              },
              onError: () => {
                TextToast.error(t("errors.unknownError"));
              },
            },
          )
        }
      />
      <ReadOnlyFileManager
        id="care-plan-prescription-file"
        label={t("diabetesForms.prescription")}
        file={carePlan.prescription}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  grid: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing(4)};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
  labelStyle: css`
    font-size: ${FontSizes.body};
  `,
  goals: css`
    white-space: pre-wrap;
  `,
  noValue: css`
    color: ${theme.palette.text.disabled};
    font-style: italic;
  `,
  icon: css`
    height: ${FontSizes.body};
    width: ${FontSizes.body};
    margin-right: 0;
  `,
  richText: css({
    maxHeight: "80vh",
  }),
});
